import { Injectable } from '@angular/core';
import { AppConfig } from '../models/app-config';
import { HttpClient } from '@angular/common/http';
import { FreeTrialEndRequestDetail, PendingChangeModel, ResourceChangeRequestDetail, ServiceFeatureModel, billingOrganisation, createUserModel } from '../models';
import { subscribedService } from '../models/service-models';
import { ApiService } from '../../../services/api.service';
import { Observable, Subscriber, firstValueFrom, map } from 'rxjs';
import { CompanyService } from '../../../services/company.service';
import { ApiResultValues, IApiResult } from '../models/apiResultModels';
import { IGroupContainer, IKeyValue, ISection, buildFormFromSection, getFormDirtyValues } from '../models/group-edit-config';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ExistingEmailValidator } from '../validators/existing-email-validator';
import { PasswordComplexityValidator } from '../validators/password-complexity-validator';
import { CompareValueValidator } from '../validators/compare-value-validator';
import { ModalEditorComponent } from '../components/modal-editor/modal-editor.component';
import { dialogDefaultFullScreen } from '../models/dialogDefaults';
import { AuthService } from '../../../services/auth.service';
import { PaymentMethodStripeComponent } from '../components/payment-method-stripe/payment-method-stripe.component';
import { LoginFormComponent } from '../components/login-form/login-form.component';
import { DialogService } from './dialog.service';
import { OrgUserCompaniesComponent } from '../../management/components/org-user-companies/org-user-companies.component';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

    constructor(private http: HttpClient,
        private api: ApiService,
        private auth: AuthService,
        private comp: CompanyService,
        private dialog: MatDialog,
        private fb: FormBuilder,
        private dlg: DialogService) { }
    
    private getBaseUrl(orgId: string) {
        return `${AppConfig.settings.apiFalServices}${orgId}`
    }

    async getServiceOfferings() {
        const url: string = `${AppConfig.settings.apiSignupUrl}getServiceOfferings/FixedAssetRegister`;
        return firstValueFrom(this.http.get<any>(url).pipe(
            
        ));
    }

    async getCurrentCountryList() {
        const url = `${AppConfig.settings.apiAuthUrl}country`;
        return firstValueFrom(this.http.get<any[]>(`${AppConfig.settings.apiAuthUrl}country`).pipe(

        ));
    }
    
    async getServices(orgId: string): Promise<IApiResult> {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/get`;
        return firstValueFrom(this.http.get<any[]>(url).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempted to get your subscriptions")
        ));
    }

    private _currentUserOrganisation: any;
    async getUserOrganisation(orgId: string, refresh?: boolean){
        if(this._currentUserOrganisation && !refresh){
            if(this._currentUserOrganisation.orgId === orgId){
                return this._currentUserOrganisation;
            }
        }

        this._currentUserOrganisation = await firstValueFrom(this.auth.getUserOrganisation(orgId));     
        return this._currentUserOrganisation;
    }


    async loginViaDialog(){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                //build a form group etc and send to dialog...
                const form = this.fb.group({
                    username: ['', [Validators.required, Validators.email]],
                    password: ['', Validators.required]
                });

                const g: IGroupContainer = {
                    dialogMode: true,
                    collapseGroupNav: false,
                    title: 'Sign-in to nettTracker',
                    description: 'If you have a user account, please sign-in to continue',
                    groups:[
                        {
                            header: 'header',
                            id: '100',
                            sections: [
                                {
                                    header: 'Sign-in',
                                    description: '',
                                    allowEdit: false,
                                    form: form,
                                    data: {},
                                    items: [],
                                    component: LoginFormComponent
                                }
                            ]
                        }
                    ],
                    cancelAction: (ref: MatDialogRef<ModalEditorComponent>) => {
                        ref.close();
                        o.next(null);
                    },
                    completeAction: async(ref: MatDialogRef<ModalEditorComponent>) => {
                        const {username, password} = form.getRawValue();
                        const data = await this.auth.login(username, password);
                        if (data.ok){
                            o.next(data);
                            ref.close();
                        }
                    }
                }

                this.dialog.open(ModalEditorComponent,
                    {
                        data: g,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncF();
        });

        return firstValueFrom(obs);
    }

    async validatePromoCode(code: string): Promise<IApiResult>{
        if(code){
            const url: string = `${AppConfig.settings.apiSignupUrl}PromoCode/${code}`;
            return firstValueFrom( this.http.get<IApiResult>(url).pipe(
                this.api.getV1MapFunction(),
                this.api.getCatchErrorFunction("An error occurred attempting to validate your promo code")
            ));
        }else{
            return {
                result: ApiResultValues.NotFound,
                data: null,
                message: 'No code supplied'
            };
        }
    }

    async createService(service: subscribedService) {
        const url: string = `${AppConfig.settings.apiSignupUrl}CreateService`;
        return firstValueFrom(this.http.post<number>(url, service).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("THere was an error creating your new subscription")       
        ));
    }

    async signupOrganisation(org: billingOrganisation) {
        const url: string = `${AppConfig.settings.apiSignupUrl}completeOrganisationSignup`;
        let model = {
            organisation: org
        };
    
        return firstValueFrom(this.http.post<any>(url, model).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("There was an error creating your organisation and subscription")
        ));
    }
    
    async updateServicePayment(orgId: string, serviceId: number, paymentMethodId: number): Promise<IApiResult> {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/updatePayment/${serviceId}/${paymentMethodId}`;
        return firstValueFrom(this.http.get<number>(url).pipe(
            map((res) =>{
                this.comp.loadCompanyList();
                return {
                    result: ApiResultValues.Ok,
                    data: res,
                    message: 'Result converted to V2 result'
                }
            }
                
            ),
            this.api.getCatchErrorFunction("An error occurred attempting to update payment method for this service")
        ));
    }

    async signup(org: billingOrganisation, user: createUserModel) {
        const url: string = `${AppConfig.settings.apiSignupUrl}completeSignup`;
        const model = {
          organisation: org,
          user: user
        };
    
        return firstValueFrom(this.http.post<any>(url, model).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("There was an error completing your signup")
        ));
    }
    
    canServiceBeModified(orgId: string ,serviceId: number){
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/canchange/${serviceId}`;
        return firstValueFrom(this.http.get<string>(url).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempting to confirm subscription can change")
        ));
    }
    
    calculateFeatureCost(orgId: string, serviceKey: number, featureKey: string, quantity: number) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/calculateFeatureCost/${serviceKey}/${featureKey}/${quantity}`;
        return firstValueFrom(this.http.get<number>(url).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempting to retrieve cost for selected features")
        ));
    }
    
    calculatePeriodServiceCost(orgId: string, serviceKey: string, featureKey: string, quantity: number, period: string, serviceId: number) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/calculatePeriodFeatureCost/${serviceKey}/${featureKey}/${quantity}/${period}/${serviceId}`;
        return firstValueFrom(this.http.get<number>(url).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempting to retieve cost for selected features")
        ));
    }
    
    calculateCostDifference(orgId: string, serviceId: number, details: ResourceChangeRequestDetail) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/CalculateCostDifference/${serviceId}`;
        return firstValueFrom(this.http.post<number>(url, details).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempting to retrieve cost difference for requested changes")
        ));
    }
    
    createCancellation(orgId: string, serviceId: number) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/createCancellation/${serviceId}`;
        return firstValueFrom(this.http.get<PendingChangeModel>(url).pipe(
            map((res) =>{
                this.comp.loadCompanyList();
                return {
                    result: ApiResultValues.Ok,
                    data: res,
                    message: 'Result converted to V2 result'
                }
            }
                
            ),
            this.api.getCatchErrorFunction("An error occurred attempting to create cancellation change request")
        ));
    }
    
    createEndFreeTrial(orgId: string, serviceId: number, detail: FreeTrialEndRequestDetail) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/createEndFreeTrial/${serviceId}`;
        return firstValueFrom(this.http.post<PendingChangeModel>(url, detail).pipe(
            map((res) =>{
                this.comp.loadCompanyList();
                return {
                    result: ApiResultValues.Ok,
                    data: res,
                    message: 'Result converted to V2 result'
                }
            }
                
            ),
            this.api.getCatchErrorFunction("An error occurred attempting to create end free trial request")
        ));
    }
    
    createChangeBillingPeriod(orgId: string, serviceId: number, period: string) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/createChangeBillingPeriod/${serviceId}/${period}`;
        return firstValueFrom(this.http.get<PendingChangeModel>(url).pipe(
            map((res) =>{
                this.comp.loadCompanyList();
                return {
                    result: ApiResultValues.Ok,
                    data: res,
                    message: 'Result converted to V2 result'
                }
            }
                
            ),
            this.api.getCatchErrorFunction("An error occurred attempting to create change billing period request")
        ));
      }
    
    createChangeResource(orgId: string, serviceId: number, details: ResourceChangeRequestDetail) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/createChangeResource/${serviceId}`;
        return firstValueFrom(this.http.post<PendingChangeModel>(url, details).pipe(
            map((res) =>{
                this.comp.loadCompanyList();
                return {
                    result: ApiResultValues.Ok,
                    data: res,
                    message: 'Result converted to V2 result'
                }
            }
                
            ),
            this.api.getCatchErrorFunction("An error occurred attempting to create change resources request")
        ));
    }
    
    retryPendingChange(orgId: string, serviceId: number) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/retry/${serviceId}`;
        return firstValueFrom(this.http.get<PendingChangeModel>(url).pipe(
            map((res) =>{
                this.comp.loadCompanyList();
                return {
                    result: ApiResultValues.Ok,
                    data: res,
                    message: 'Result converted to V2 result'
                }
            }
                
            ),
            this.api.getCatchErrorFunction("An error occurred attempting to retry pending request")
        ));
    }
    
    cancelPendingChange(orgId: string, serviceId: number) {
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/cancel/${serviceId}`;
        return firstValueFrom(this.http.get<PendingChangeModel>(url).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempting to cancel the pending request")
        ));
    }
    
    validateUserSubscriptionChange(serviceId: number, details: ResourceChangeRequestDetail) {
        const url = `${AppConfig.settings.apiValidateUrl}ValidateChanges/${serviceId}`;
        return firstValueFrom(this.http.post<boolean>(url, details).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred validating subscription change")
        ));
      }
    
    removeSuspension(orgId: string, serviceId: number){
        const url = `${this.getBaseUrl(orgId)}/Subscriptions/releaseSuspension/${serviceId}`;
        return firstValueFrom(this.http.post<IApiResult>(url, null).pipe(
            this.api.getV1MapFunction(),
            this.api.getCatchErrorFunction("An error occurred attempting to release suspension")
        ));
    }
    
    formatChangeType(type: string){
        if(type){
            if(type === 'BillingPeriodChange'){
                return "Billing Period Change";
            }else if(type === 'Cancellation'){
                return "Subscription Cancellation";
            }else if(type === 'ResourceAllocationReduction'){
                return "Reduction in Subscription Resources";
            }else{
                return type;
            }
        }else{
            return '';
        }
    }
    
    async getAvailableServiceFeatures(data: ApiService){
        const res = await data.getServiceOfferings();
        const template = JSON.parse(res.data.optionDefinitions) as subscribedService;

        const options = template.serviceOptions;

        const features: ServiceFeatureModel[] = [];
        options.forEach(opt => {
            const feature = new ServiceFeatureModel();
            feature.cost = 0;
            feature.featureDescripion = opt.featureDescription;
            feature.featureKey = opt.featureKey;
            feature.featureName = opt.featureName;
            feature.quantity = 0;
            feature.usage = 0;

            features.push(feature);
        });

        return features;
    }

    async createPaymentMethod(org: billingOrganisation, service: subscribedService){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const dialogData = {
                    organisation: org,
                    service
                };

                let dref: MatDialogRef<ModalEditorComponent>;

                const postDialog = async(method: any) =>{
                    dref.close();
                    if(method){
                        if(service){
                            const result = await this.updateServicePayment(org.safeId, service.id, method.id);
                        }

                        o.next(method);
                    }                    
                }

                const g: IGroupContainer = {
                    title: 'Create Payment Method',
                    description: 'Please provide the details of the new credit or payment card that you wish to use for your subscription.',
                    dialogMode: true,
                    actionText: 'Close (no saving)',
                    collapseGroupNav: true,
                    hideCancelButton: true,
                    data: dialogData,
                    form: this.fb.group({}),
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [
                                {
                                    header: '',
                                    description: '',
                                    allowEdit: false,
                                    data: dialogData,
                                    form: this.fb.group({}),
                                    items: [],
                                    component: PaymentMethodStripeComponent,
                                    componentProps: [{
                                        propName: 'organisation', 
                                        data: org
                                    }],
                                    componentOutputs: [
                                        {
                                            outputName: 'methodCreated',
                                            handler: (method: any) => {
                                               postDialog(method);
                                            }
                                        }
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (ref: MatDialogRef<ModalEditorComponent>) => {
                        ref.close();
                        o.next(null);
                    },
                    completeAction: async(ref: MatDialogRef<ModalEditorComponent>) => {
                        ref.close();
                        o.next(null);
                    }
                }
        
                dref = await this.dialog.open(ModalEditorComponent,
                    {
                        data: g,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async assignPaymentToService(org: billingOrganisation, paymentMethodId: number){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const services = await this.getServices(org.id);

                const ddl = (services.data as Array<any>).map(s => {
                    const i: IKeyValue = {
                        key: s.id.toString(),
                        value: `${s.id}: ${s.serviceName}`
                    };
                    return i;
                })
                
                const form = this.fb.group({
                    serviceId: ['', Validators.required]
                });
                
                const g: IGroupContainer = {
                    title: 'Assign Payment to Subscription',
                    dialogMode: true,
                    collapseGroupNav: true,
                    description: 'Assign this payment method to the selected service.',
                    data: {},
                    form: form,
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Select subscription',
                                    description: '',
                                    allowEdit: false,
                                    data: {},
                                    form: form,
                                    items: [
                                        {label: 'Subscription',fieldName: 'serviceId', 
                                            control: 'select',
                                            dropdownList: ddl
                                        }
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (ref: MatDialogRef<ModalEditorComponent>) => {
                        ref.close();
                        o.next(null);
                    },
                    completeAction: async(ref: MatDialogRef<ModalEditorComponent>) => {
                        const {serviceId} = form.getRawValue();
                        const res = await this.api.updateServicePayentMethod(org.id, serviceId, paymentMethodId)

                        if(res.result === ApiResultValues.Ok){
                            o.next(res.data);
                            ref.close();
                        }
                    }
                }

                this.dialog.open(ModalEditorComponent, {
                    data: g,
                    ...dialogDefaultFullScreen
                });
            }
            asyncF();
        });

        return await firstValueFrom(obs);
    }

    createOrgUser(org: billingOrganisation){
        this.updateOrgUser(null, org);
    }

    

    async updateOrgUser(u: any, org: billingOrganisation)
    {
        const obs = new Observable<boolean>(o => {
            let user;
            const uiRole = {
                canManageUsers: false,
                canManageSubs: false,
                canManagePayments: false
            };
            
            let newUser = false;

            if(u){
                
                const role = u.organisations.find(x=>x.orgId === org.safeId);
                uiRole.canManageUsers = role.permissionAddUser && role.permissionModifyUser && role.permissionDeleteUser && role.permissionUpdatePassword;
                uiRole.canManageSubs = role.permissionAddService && role.permissionAmendService && role.permissionCancelService;
                uiRole.canManagePayments = role.permissionAddPayment && role.permissionMakePayment && role.permissionRemovePayment;

                user = {
                    user: u,
                    role: {
                        ...role,
                        ...uiRole
                    }
                };

            }else{
                newUser = true;
                user = {
                    user: {
                        region: 'en-GB',
                        language: 'en',
                        accountVerified: true,
                        safeId: null
                    },
                    role: uiRole
                };
            }

            const form = this.fb.group({
                user: this.fb.group({}),
                role: this.fb.group({})
            });

            const userSection: ISection = {
                header: 'User information',
                allowEdit: false,
                data: {},
                form: form.controls['user'],
                items: [
                    {
                        label: 'Email',
                        fieldName: 'email',
                        control: 'input',
                        isFieldReadOnly: !newUser,
                        controlDataType: 'email',
                        validation: 'Required to be unique',
                        validatorsSync: [Validators.email],
                        validatorsAsync: [ExistingEmailValidator.validateEmailFactory(this.auth)]
                    },
                    {
                        label: 'Full name',
                        fieldName:'fullName',
                        control: 'input',
                        validation: 'Required',
                        isFieldRequired: true
                    },
                    {
                        label: 'Mobile number',
                        fieldName: 'mobileNumber',
                        control: 'input',
                        isFieldRequired: true
                    }
                ]
            }

            if(newUser){
                userSection.items.push({
                        label: 'Password',
                        fieldName: 'password',
                        control: 'input',
                        controlDataType: 'password',
                        validation: 'Required',
                        validatorsSync:  [Validators.required, PasswordComplexityValidator.passwordComplexityValidatorFactory()],
                    });
                userSection.items.push({
                        label: 'Confirm password',
                        fieldName: 'confirmPassword',
                        control: 'input',
                        controlDataType: 'password',
                        validation: 'Passwords do not match',
                        validatorsSync: [Validators.required, CompareValueValidator.compareValueValidatorFactory("password")]
                    });
            }
            buildFormFromSection(userSection, user.user, form.controls['user']);

            const roleSection: ISection = {
                header: 'Permissions',
                allowEdit: false,
                data: {},
                form: form.controls['role'],
                items: [
                    {
                        label: 'Allow user management',
                        fieldName: 'canManageUsers',
                        control: 'checkbox'
                    },
                    {
                        label: 'Allow subscription management',
                        fieldName: 'canManageSubs',
                        control: 'checkbox'
                    },
                    {
                        label: 'Allow payment management',
                        fieldName: 'canManagePayments',
                        control: 'checkbox'
                    }
                ]
            }
            buildFormFromSection(roleSection, user.role, form.controls['role']);

            const cfg: IGroupContainer = {
                title: newUser ? 'Create user' : 'Update user',
                description: (newUser ? 'Create ' : 'Update ') + 'user for ' + org.organisationName,
                dialogMode: true,
                collapseGroupNav: true,
                data: user,
                form: form,
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(false);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const updatedUser: any = {};
                    getFormDirtyValues(form, updatedUser);

                    if(Object.keys(updatedUser).length === 0){
                        dRef.close();
                        return;
                    }

                    //updated user should have role & user elements
                    if(updatedUser.user){
                        user.user = {
                            ...user.user,
                            ...updatedUser.user,
                            safeId: user.user.id
                        };
                    }

                    if(updatedUser.role){
                        const keys = Object.keys(updatedUser.role);
                        keys.forEach(k => {
                            const v = updatedUser.role[k];
                            if(k === 'canManageUsers'){
                                user.role.permissionAddUser = v;
                                user.role.permissionDeleteUser = v;
                                user.role.permissionModifyUser = v;
                                user.role.permissionUpdatePassword = v;
                            }
                            if(k === 'canManagePayments'){
                                user.role.permissionAddPayment = v;
                                user.role.permissionMakePayment = v;
                                user.role.permissionRemovePayment = v;
                            }
                            if(k === 'canManageSubs'){
                                user.role.permissionAddService = v;
                                user.role.permissionAmendService = v;
                                user.role.permissionCancelService = v;
                            }
                        })
                    }
                    
                    let result: IApiResult;
                    if(newUser){
                        result = await this.api.createOrgUser(user, org.safeId);
                    }else{
                        result = await this.api.updateOrgUser(user, org.safeId);
                        
                    }

                    if(result.result === ApiResultValues.Ok){
                        dRef.close();
                        o.next(true);
                    }
                    
                    
                },
                groups: [
                    {
                        header: 'User details',
                        id: '1001',
                        sections: [
                            userSection,
                            roleSection
                        ]
                    }
                ]

            }

            this.dialog.open(ModalEditorComponent,
                {
                    data: cfg,
                    ...dialogDefaultFullScreen
                }
            );
        });

        return firstValueFrom(obs);

    }

    async updateOrgUserPassword(u: any, org: billingOrganisation){
        const obs = new Observable<boolean>(o => {
            const role = u.organisations.find(x=>x.orgId === org.safeId);
            let user  = {
                user: u,
                role
            };
            const form = this.fb.group({});

            const userSection: ISection = {
                header: 'User information',
                allowEdit: false,
                data: {},
                form: form,
                items: [
                    {
                        label: 'Password',
                        fieldName: 'proposedPassword',
                        control: 'input',
                        controlDataType: 'password',
                        validation: 'Required',
                        validatorsSync:  [Validators.required, PasswordComplexityValidator.passwordComplexityValidatorFactory()],
                    },{
                        label: 'Confirm password',
                        fieldName: 'confirmPassword',
                        control: 'input',
                        controlDataType: 'password',
                        validation: 'Passwords do not match',
                        validatorsSync: [Validators.required, CompareValueValidator.compareValueValidatorFactory("proposedPassword")]
                    }
                ]
            }

            buildFormFromSection(userSection, user.user, form);

            const cfg: IGroupContainer = {
                title: 'Update user password',
                description: `Update user password for ${user.user.fullName}, part of ${org.organisationName}`,
                dialogMode: true,
                collapseGroupNav: true,
                data: user,
                form: form,
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(false);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const updatedUser: any = {};
                    getFormDirtyValues(form, updatedUser);

                    if(Object.keys(updatedUser).length === 0){
                        dRef.close();
                        return;
                    }

                    updatedUser.userId = user.user.id;
                    updatedUser.currentPassword = updatedUser.proposedPassword;
                    updatedUser.serviceDescriptor = AuthService.serviceDescriptor;
                    
                    let result: IApiResult  = await this.api.updateOrgUserPassword(updatedUser, org.safeId);

                    if(result.result === ApiResultValues.Ok){
                        dRef.close();
                        o.next(true);
                    }
                },
                groups: [
                    {
                        header: 'User password',
                        id: '1001',
                        sections: [
                            userSection
                        ]
                    }
                ]

            }

            this.dialog.open(ModalEditorComponent,
                {
                    data: cfg,
                    ...dialogDefaultFullScreen
                }
            );
        });

        return firstValueFrom(obs);
    }

    async updateOrgUserCompanies(u: any, org: billingOrganisation){
        const obs = new Observable<boolean>(o => {
            const f = async() => {  
                const companiesRes = await this.api.getOrganisationUserCompanies(org.safeId, u.id);

                if(companiesRes.result !== ApiResultValues.Ok){
                    this.dlg.showOk('Error retrieving user copmanies', 'There was an error retrieving the companies for this organisation and user');;
                    o.next(false);
                    return;
                }

                const companies = companiesRes.data;

                const g: IGroupContainer = {
                    title: `Update user company roles for ${u.fullName} (${u.email})`,
                    description: 'Please update the ',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: companies,
                    form: this.fb.group({}),
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Companies',
                                    description: '',
                                    allowEdit: false,
                                    data: companies,
                                    form: this.fb.group({}),
                                    items: [],
                                    component: OrgUserCompaniesComponent,
                                    componentProps: [{
                                        propName: 'companies',
                                        data: companies
                                    }],
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(false);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const model = {
                            userId: u.id,
                            name: u.fullName,
                            email: u.email,
                            companies: companies
                        };
                        const res = await this.api.updateOrgUserCompanies(org.id, model);
                        if(res.result === ApiResultValues.Ok){
                            dRef.close();
                            o.next(true);
                        }
                    }
                };

                this.dialog.open(ModalEditorComponent,
                    {
                        data: g,
                        ...dialogDefaultFullScreen
                    });

            };

            

            f();
        });

        return await firstValueFrom(obs);
    }

    
}
